import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { questionLabel } from "@/lib/questionOrdinals";
import { displayDomainLabel } from "@/reports/telecom/displayDomainLabel";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ClipboardCheck,
  Link2,
  ShieldCheck,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  XCircle,
  PauseCircle,
  Loader2,
  MessageSquare,
} from "lucide-react";
import { normalizeEvidenceUrl } from "@/lib/capExampleEvidence";
import { resolveFindingControlLabel } from "@/lib/findingControlLabel";
import { resolveFindingFrameworkPills } from "@/lib/findingFrameworkPills";
import { FindingFrameworkPills } from "@/components/reviewer/FindingFrameworkPills";
import { CAP_STATUS_META, normalizeCapStatus } from "@/components/cap/capStatus";
import { FindingThread } from "@/components/cap/FindingThread";
import { FindingHistory } from "@/components/cap/FindingHistory";
import { UnifiedEvidence } from "@/components/cap/UnifiedEvidence";
import { FindingContextBlock } from "@/components/cap/FindingContextBlock";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDueDate } from "@/lib/dueDate";

const sb: any = supabase as any;

interface EvidenceLink {
  id: string;
  link_url: string;
  created_at?: string | null;
  uploaded_at?: string | null;
  uploaded_by_role?: string | null;
}

interface CapFinding {
  id: string;
  display_id?: string | null;
  display_q_number?: number | null;
  display_seq?: number | null;
  control_code?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  timeframe?: string | null;
  recommended_action?: string | null;
  recommended_measures?: string | null;
  owner?: string | null;
  owners?: string[] | null;
  due_date?: string | null;
  cap_status?: string | null;
  respondent_notes?: string | null;
  reviewer_comment?: string | null;
  validation_status?: string | null;
  validated_at?: string | null;
  updated_at?: string | null;
  csv_domain?: string | null;
  nist_function?: string | null;
  source_type?: string | null;
  source_key?: string | null;
  infrastructure_control_key?: string | null;
  remediation_path?: string | null;
  justification?: string | null;
  observed_gap?: string | null;
  recommended_additional_evidence?: string | null;
  evidence_reviewed?: string | null;
  evidence_links?: EvidenceLink[];
}

interface CapPayload {
  assessment_id: string;
  version: number;
  published_at: string | null;
  published_by: string | null;
  findings: CapFinding[];
}

const PRIORITY_COLORS: Record<string, string> = {
  Immediate: "bg-destructive/10 text-destructive border-destructive/30",
  "Quick Win": "bg-yellow-500/10 text-yellow-700 border-yellow-500/30",
  Strategic: "bg-blue-500/10 text-blue-700 border-blue-500/30",
};

const VALIDATION_LABELS: Record<string, { label: string; className: string }> = {
  accepted: { label: "Accepted", className: "bg-green-600 text-white" },
  needs_more_evidence: { label: "Needs More Evidence", className: "bg-yellow-500 text-white" },
  rejected: { label: "Rejected", className: "bg-destructive text-destructive-foreground" },
  deferred: { label: "Review Deferred", className: "bg-muted text-muted-foreground" },
  not_reviewed: { label: "Not Reviewed", className: "bg-muted text-muted-foreground" },
};

interface Props {
  assessmentId: string;
  questionnaireKey?: string | null;
  disabled?: boolean;
}

export const CorrectiveActionPlanTab: React.FC<Props> = ({
  assessmentId,
  questionnaireKey,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const isTelecomV2 = questionnaireKey === "telecom_v2";
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<CapPayload | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!assessmentId || !isTelecomV2) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await sb.rpc("reviewer_get_action_plan", {
        p_assessment_id: assessmentId,
      });
      if (error) {
        setError(error.message);
        setPayload(null);
      } else {
        setPayload(data as CapPayload);
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to load Action Plan");
    } finally {
      setLoading(false);
    }
  }, [assessmentId, isTelecomV2]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const { error } = await sb.rpc("reviewer_publish_action_plan", {
        p_assessment_id: assessmentId,
      });
      if (error) throw error;
      toast.success("Action Plan published");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to publish Action Plan");
    } finally {
      setPublishing(false);
    }
  };

  const handleExport = async (kind: "csv" | "pdf") => {
    if (!payload) return;
    try {
      if (kind === "csv") {
        const { exportActionPlanCsv } = await import(
          "@/reports/actionPlan/exportActionPlan"
        );
        exportActionPlanCsv(payload);
        return;
      }

      // PDF: use the new professional CAP report generator (Phase 3),
      // matching the on-screen CAP Preview layout. Pull lightweight
      // context (client name, frameworks, reviewer name) from the same
      // RPCs the Preview page uses.
      const [{ generateCapReport }, profileRes, detailRes, userRes] = await Promise.all([
        import("@/reports/actionPlan/generateCapReport"),
        sb.rpc("reviewer_get_assessment_profile", { p_assessment_id: assessmentId }).then(
          (r: any) => r,
          () => ({ data: null }),
        ),
        sb.rpc("reviewer_get_assessment_detail", { p_assessment_id: assessmentId }).then(
          (r: any) => r,
          () => ({ data: null }),
        ),
        sb.auth.getUser(),
      ]);

      const profile = Array.isArray(profileRes?.data) ? profileRes.data[0] : profileRes?.data;
      const detail = Array.isArray(detailRes?.data) ? detailRes.data[0] : detailRes?.data;
      const meta = userRes?.data?.user?.user_metadata ?? {};
      const reviewerName =
        detail?.reviewer_name ||
        detail?.reviewer_full_name ||
        meta.full_name ||
        meta.name ||
        userRes?.data?.user?.email ||
        "";
      const frameworks =
        (profile?.methodology_frameworks ?? []).filter(Boolean).join(" / ") ||
        profile?.methodology ||
        "";

      generateCapReport(payload as any, {
        clientName: profile?.client_organization || "Client Organization",
        parentOrganization: profile?.parent_organization ?? null,
        sector: profile?.sector ?? null,
        assessmentName: detail?.questionnaire_id ?? questionnaireKey ?? "",
        questionnaireId: detail?.questionnaire_id ?? questionnaireKey ?? "",
        frameworks,
        reviewerName,
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed");
    }
  };

  const findings = payload?.findings ?? [];
  const isPublished = Boolean(payload?.published_at);

  const stats = useMemo(() => {
    const total = findings.length;
    const validated = findings.filter((f) => f.validation_status === "accepted").length;
    const pending = findings.filter(
      (f) => !f.validation_status || f.validation_status === "not_reviewed"
    ).length;
    const withEvidence = findings.filter((f) => (f.evidence_links?.length ?? 0) > 0).length;
    return { total, validated, pending, withEvidence };
  }, [findings]);

  if (!isTelecomV2) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        <ClipboardCheck className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="font-medium">Corrective Action Plan is only available for telecom_v2.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        Loading Corrective Action Plan…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header bar with publish controls */}
      <div className="bg-card rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            Corrective Action Plan
            {isPublished && payload && (
              <Badge className="bg-primary text-primary-foreground text-[10px]">
                v{payload.version} · Published
              </Badge>
            )}
            {!isPublished && (
              <Badge variant="outline" className="text-[10px]">Not yet published</Badge>
            )}
          </h3>
          <p className="text-xs text-muted-foreground max-w-2xl">
            Click any row to expand the Details, Conversation, Evidence, and History
            sub-tabs for that finding. All remediation activity is managed here.
          </p>
          {payload?.published_at && (
            <p className="text-[11px] text-muted-foreground">
              Last published {new Date(payload.published_at).toLocaleString()}
            </p>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => load()} disabled={loading}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          {isPublished && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/reviewer/cap/${assessmentId}/preview`)}
              >
                Preview CAP
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleExport("csv")}>
                Export CSV
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleExport("pdf")}>
                Export PDF
              </Button>
            </>
          )}
          <Button
            size="sm"
            onClick={handlePublish}
            disabled={disabled || publishing || findings.length === 0}
          >
            {publishing
              ? "Publishing…"
              : isPublished
              ? `Republish (v${(payload?.version ?? 0) + 1})`
              : "Publish Action Plan"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Items" value={stats.total} />
        <StatCard
          label="Accepted"
          value={stats.validated}
          className="bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400"
        />
        <StatCard
          label="Pending Validation"
          value={stats.pending}
          className="bg-yellow-50 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400"
        />
        <StatCard
          label="With CAP Evidence"
          value={stats.withEvidence}
          className="bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400"
        />
      </div>

      {/* CAP table — single Action Register, sorted by due date (RPC order) */}
      {findings.length === 0 ? (
        <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
          <p className="font-medium">No findings to publish yet.</p>
          <p className="text-sm mt-1">
            Create findings in the Findings tab and refine them in the Remediation Roadmap.
          </p>
        </div>
      ) : (
        <ActionRegisterTable
          findings={findings}
          expandedRows={expandedRows}
          toggleRow={toggleRow}
          onValidated={load}
        />
      )}
    </div>
  );
};

interface ActionRegisterTableProps {
  findings: CapFinding[];
  expandedRows: Set<string>;
  toggleRow: (id: string) => void;
  onValidated: () => void;
}

/**
 * Single Action Register table.
 *
 * Findings are rendered in the exact order returned by the RPC:
 *   due_date ASC (nulls last) → priority (Immediate, Quick Win, Strategic, Other)
 *   → display_seq → created_at
 *
 * Priority is shown as a column badge. No client-side sorting or grouping.
 */
const ActionRegisterTable: React.FC<ActionRegisterTableProps> = ({
  findings,
  expandedRows,
  toggleRow,
  onValidated,
}) => {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Sorted by due date. Items without a due date appear last. Priority shown as a column.
      </p>
      <div className="bg-card rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]" />
              <TableHead className="w-[90px]">Control</TableHead>
              <TableHead>Finding</TableHead>
              <TableHead className="w-[110px]">Owner</TableHead>
              <TableHead className="w-[110px]">Due Date</TableHead>
              <TableHead className="w-[90px]">Priority</TableHead>
              <TableHead className="w-[140px]">Respondent Status</TableHead>
              <TableHead className="w-[150px]">Reviewer Validation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {findings.map((f) => {
              const evidenceCount = f.evidence_links?.length ?? 0;
              const validation = f.validation_status
                ? VALIDATION_LABELS[f.validation_status]
                : null;
              const isExpanded = expandedRows.has(f.id);
              const capStatus = f.cap_status || "open";

              return (
                <React.Fragment key={f.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => toggleRow(f.id)}
                  >
                    <TableCell>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className={f.source_type === "infrastructure_security" ? "text-xs" : "font-mono text-xs"}>
                      {resolveFindingControlLabel(f) || "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-start gap-2">
                        {f.display_id && (
                          <span
                            className="shrink-0 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary"
                            title="Finding tracking reference (persistent within this assessment)"
                          >
                            {f.display_id}
                          </span>
                        )}
                        {f.display_q_number != null && (
                          <span
                            className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold text-muted-foreground"
                            title="Frozen source question number for this assessment"
                          >
                            {questionLabel(f.display_q_number)}
                          </span>
                        )}
                        <div className="font-medium">
                          {f.finding_title || (
                            <span className="italic text-muted-foreground">No title</span>
                          )}
                        </div>
                      </div>
                      {(f.csv_domain || f.source_type) && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {displayDomainLabel(f.csv_domain, f.source_type)}
                        </div>
                      )}
                      <div className="mt-1">
                        <FindingFrameworkPills
                          pills={resolveFindingFrameworkPills({
                            source_type: f.source_type,
                            source_key: (f as any).source_key ?? (f as any).infrastructure_control_key ?? null,
                            nist_function: f.nist_function,
                          })}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {Array.isArray(f.owners) && f.owners.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {f.owners.map((o) => (
                            <Badge key={o} variant="secondary" className="text-[10px]">{o}</Badge>
                          ))}
                        </div>
                      ) : f.owner ? (
                        f.owner
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {f.due_date ? (
                        formatDueDate(f.due_date) || f.due_date
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 ${
                          PRIORITY_COLORS[f.priority_level || ""] ?? ""
                        }`}
                      >
                        {f.priority_level || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <CapStatusBadge status={capStatus} />
                      {evidenceCount > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                          <Link2 className="h-2.5 w-2.5" />
                          {evidenceCount} evidence link{evidenceCount === 1 ? "" : "s"}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {validation && f.validation_status !== "not_reviewed" ? (
                        <Badge className={`${validation.className} text-[10px] gap-1`}>
                          <ShieldCheck className="h-2.5 w-2.5" />
                          {validation.label}
                        </Badge>
                      ) : normalizeCapStatus(capStatus) === "awaiting_reviewer" ? (
                        <span className="text-xs text-yellow-700 font-medium">
                          Awaiting Reviewer
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Pending</span>
                      )}
                      {f.validated_at && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(f.validated_at).toLocaleDateString()}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                      <TableCell />
                      <TableCell colSpan={7} className="py-4">
                        <ExpandedDetails finding={f} onValidated={onValidated} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};




const StatCard: React.FC<{ label: string; value: number; className?: string }> = ({
  label,
  value,
  className = "bg-card",
}) => (
  <div className={`rounded-lg border p-3 ${className}`}>
    <div className="text-2xl font-semibold">{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
  </div>
);


function CapStatusBadge({ status }: { status: string }) {
  const norm = normalizeCapStatus(status);
  const meta = CAP_STATUS_META[norm];
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${meta.className}`}>
      {meta.label}
    </Badge>
  );
}

const VALIDATION_BUTTON_META = [
  { value: "accepted", label: "Accept", icon: CheckCircle2, variant: "default" as const, requiresComment: false },
  { value: "needs_more_evidence", label: "Request More Evidence", icon: AlertCircle, variant: "outline" as const, requiresComment: true },
  { value: "rejected", label: "Reject", icon: XCircle, variant: "destructive" as const, requiresComment: true },
  { value: "deferred", label: "Defer", icon: PauseCircle, variant: "outline" as const, requiresComment: false },
];

function ExpandedDetails({
  finding,
  onValidated,
}: {
  finding: CapFinding;
  onValidated: () => void;
}) {
  const [comment, setComment] = useState(finding.reviewer_comment ?? "");
  const [submitting, setSubmitting] = useState<string | null>(null);
  const capStatus = finding.cap_status || "open";
  const canValidate = normalizeCapStatus(capStatus) === "awaiting_reviewer";
  const action = (finding.recommended_action || finding.recommended_measures || "").trim();

  useEffect(() => {
    setComment(finding.reviewer_comment ?? "");
  }, [finding.id, finding.reviewer_comment]);

  const handleValidate = async (validationStatus: string, requiresComment: boolean) => {
    if (requiresComment && !comment.trim()) {
      toast.error("Please provide a reviewer comment for this action.");
      return;
    }
    setSubmitting(validationStatus);
    try {
      const { error } = await sb.rpc("reviewer_validate_action_item", {
        p_finding_id: finding.id,
        p_validation_status: validationStatus,
        p_comment: comment.trim() || null,
      });
      if (error) throw error;
      const labelMap: Record<string, string> = {
        accepted: "Accepted",
        needs_more_evidence: "Marked as needing more evidence",
        rejected: "Rejected",
        deferred: "Deferred",
      };
      toast.success(labelMap[validationStatus] ?? "Validation saved");
      onValidated();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to save validation");
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Tabs defaultValue="details" className="w-full">
      <TabsList className="h-9">
        <TabsTrigger value="details" className="text-xs">Details</TabsTrigger>
        <TabsTrigger value="conversation" className="text-xs">Conversation</TabsTrigger>
        <TabsTrigger value="evidence" className="text-xs">Evidence</TabsTrigger>
        <TabsTrigger value="history" className="text-xs">History</TabsTrigger>
      </TabsList>

      <TabsContent value="details" className="space-y-4 mt-3">
        <FindingContextBlock finding={finding} variant="reviewer" />

        {action && (
          <div className="rounded-md border bg-background p-3">
            <div className="text-xs font-semibold text-foreground mb-1">Required Action</div>
            <p className="text-xs text-foreground whitespace-pre-wrap">{action}</p>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
            Respondent Progress Summary
          </div>
          {finding.respondent_notes ? (
            <p className="text-xs text-foreground whitespace-pre-wrap bg-card rounded border p-2.5">
              {finding.respondent_notes}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground italic">No progress summary submitted yet</p>
          )}
        </div>

        <div className="border-t pt-4">
          <div className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
            Reviewer Validation
          </div>

          {finding.validation_status && finding.validation_status !== "not_reviewed" && (
            <div className="mb-3 rounded-md border bg-card p-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                {VALIDATION_LABELS[finding.validation_status] && (
                  <Badge
                    className={`${VALIDATION_LABELS[finding.validation_status].className} text-[10px] gap-1`}
                  >
                    <ShieldCheck className="h-2.5 w-2.5" />
                    {VALIDATION_LABELS[finding.validation_status].label}
                  </Badge>
                )}
                {finding.validated_at && (
                  <span className="text-[10px] text-muted-foreground">
                    Validated {new Date(finding.validated_at).toLocaleString()}
                  </span>
                )}
              </div>
              {finding.reviewer_comment && (
                <p className="text-xs mt-1.5 whitespace-pre-wrap text-foreground">
                  {finding.reviewer_comment}
                </p>
              )}
            </div>
          )}

          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Reviewer comment (required for Reject and Request More Evidence)…"
            className="min-h-[60px] text-sm mb-2"
            disabled={!canValidate}
          />

          {canValidate ? (
            <div className="flex flex-wrap items-center gap-2">
              {VALIDATION_BUTTON_META.map(({ value, label, icon: Icon, variant, requiresComment }) => (
                <Button
                  key={value}
                  size="sm"
                  variant={variant}
                  onClick={() => handleValidate(value, requiresComment)}
                  disabled={submitting !== null}
                >
                  {submitting === value ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Icon className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {label}
                </Button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              Validation actions become available once the respondent marks this item as
              Awaiting Reviewer.
            </p>
          )}
        </div>
      </TabsContent>

      <TabsContent value="conversation" className="mt-3">
        <FindingThread
          findingId={finding.id}
          canRequest
          canFulfill={false}
          composerHint="Use this conversation to communicate remediation guidance, evidence requests, and validation decisions."
        />
      </TabsContent>

      <TabsContent value="evidence" className="mt-3">
        <UnifiedEvidence findingId={finding.id} canRequest={false} canFulfill={false} />
      </TabsContent>

      <TabsContent value="history" className="mt-3">
        <FindingHistory findingId={finding.id} />
      </TabsContent>
    </Tabs>
  );
}

export default CorrectiveActionPlanTab;
